import { FC } from "react";
import { useParams } from "react-router-dom";
import { Paper } from "@mui/material";
import {
  useFetchEmployee,
  useFetchEmployeeBalances,
} from "src/Pages/Company/Absences/api/useEmployee";
import { useFetchEmployeeAbsenceRequest } from "src/Pages/Company/Absences/api/useAbsenceRequest";
import { useGetAbsenceType } from "src/Pages/Company/Absences/api/useAbsenceTypes";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import AbsenceRequestForm from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";

const AbsenceRequestEdit: FC = () => {
  const { absenceRequestId, employeeId } = useParams<Record<string, string>>();
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRequestEdit.tsx",
    });
  }

  if (!absenceRequestId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request ID provided.",
      file: "AbsenceRequestEdit.tsx",
    });
  }

  const {
    data: employee,
    isFetching: isFetchingEmployee,
    isError: isErrorEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: absenceRequest,
    isFetching: isFetchingAbsenceRequest,
    isError: isErrorAbsenceRequest,
  } = useFetchEmployeeAbsenceRequest(absenceRequestId!);

  const {
    data: absenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceRequest?.absenceType.absenceTypeId);

  if (!isFetchingEmployee && isFetchingAbsenceRequest) return <Loader />;

  if (isErrorEmployee || isErrorAbsenceRequest || isErrorAbsenceType)
    return <ErrorDataFetching />;

  if (!absenceRequest || !employee) return null;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Lista wniosków"),
            to: `${basePath}/absences/employee/${employeeId}#requests`,
          },
          {
            label: __("Edycja wniosku"),
          },
        ]}
      />
      <Title
        title={`${__("Edycja wniosku")} ${absenceRequest.absenceType.name}`}
      />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper
        sx={{ marginTop: "2rem", p: "2rem" }}
        data-testid="absence-request-edit"
      >
        <Fieldset legend={__("Status")}>
          <StatusChip variant={absenceRequest.status} />
        </Fieldset>
        {isFetchingAbsenceType && <Loader />}
        {absenceType && (
          <AbsenceRequestForm
            absenceType={absenceType}
            employeeId={employeeId}
            employeeDailyWorkingHours={employee.dailyWorkingHours}
            absenceRequestData={absenceRequest}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestEdit;
